import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page" className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Careers</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Work on problems
              <br />
              you’d happily explain
              <br />
              <span className="italic text-ink/60">at a dinner.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              We hire senior-heavy, slowly, and for the long run. If you’re reading
              this, we probably already share a few friends. Introduce yourself.
            </p>
            <Link
              to="/life"
              data-testid="careers-life-cta"
              className="mt-8 inline-flex items-center gap-2 link-underline text-sm"
            >
              Life at ThinkRight <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="py-20 md:py-28">
        <div className="tr-container">
          <div className="flex items-baseline justify-between mb-10">
            <div className="overline text-vermilion">Open roles</div>
            <div className="font-mono text-xs text-ink/50">
              {loading ? "Loading…" : `${jobs.length} positions`}
            </div>
          </div>

          <ul className="border-t border-border" data-testid="jobs-list">
            {jobs.map((j, i) => (
              <Reveal key={j.id} as="li" delay={i} className="border-b border-border">
                <Link
                  to={`/careers/${j.id}`}
                  data-testid={`job-${j.id}`}
                  className="grid grid-cols-12 gap-4 py-8 md:py-10 items-center group hover:bg-muted/60 transition-colors px-2 -mx-2"
                >
                  <div className="col-span-12 md:col-span-1 font-mono text-xs text-ink/40">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.05] group-hover:text-vermilion transition-colors">
                      {j.title}
                      <span className="text-ink/40"> · {j.level}</span>
                    </h3>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl line-clamp-2">
                      {j.summary}
                    </p>
                    {j.focus && j.focus.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {j.focus.slice(0, 4).map((f) => (
                          <span
                            key={f}
                            className="inline-block px-2 py-1 border border-ink/15 text-[10px] uppercase tracking-[0.18em] text-ink/60"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-2 text-xs uppercase tracking-[0.18em] text-ink/60">
                    {j.department}
                    <div className="mt-1 text-ink/40 normal-case tracking-normal text-[11px]">
                      {j.location}
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-3 text-xs text-ink/70">
                    <div className="text-[11px] text-ink/50">
                      {j.experience}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end">
                    <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-vermilion" />
                  </div>
                </Link>
              </Reveal>
            ))}
            {!loading && jobs.length === 0 && (
              <li className="py-16 text-center text-ink/50">
                We’re between hiring windows — please write to us anyway.
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* DONT SEE YOUR ROLE */}
      <section className="py-24 md:py-32 border-t border-border bg-ink text-white">
        <div className="tr-container grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-vermilion mb-6">Speculative</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Didn’t see your role?
              <br />
              <span className="italic text-white/60">Introduce yourself anyway.</span>
            </h2>
            <p className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">
              The best engineers we’ve hired did not apply to a job — they wrote a
              thoughtful note. If that’s you, we’ll write back.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="careers-speculative-cta"
              className="inline-flex items-center gap-2 border border-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
            >
              Write to us <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
